import { read, applyProperties, applyArtwork } from '@theme/product-reorder-prefill';

/**
 * @typedef {Object} SbSimpleProductRefs
 * @property {HTMLInputElement} variantId
 * @property {HTMLButtonElement} addToCartBtn
 * @property {HTMLElement} addToCartText
 * @property {HTMLImageElement} [productImage]
 * @property {HTMLElement} [productPrice]
 * @property {HTMLElement} [compareAtWrap]
 * @property {HTMLElement} [compareAtPrice]
 */

/**
 * @param {number} cents
 * @returns {string}
 */
const formatMoneyFromCents = (cents) => {
  const dollars = (cents / 100).toFixed(2);
  return `$${Number(dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

class SbSimpleProductForm extends HTMLElement {
  /** @type {Array<Object>} */
  #variants = [];

  connectedCallback() {
    this.#parseDataAttributes();
    this.#cacheRefs();
    this.#bindEvents();
    this.#bindReviews();
    this.#prefillFromUrl();
  }

  /**
   * Reflects a re-order deep-link (see @theme/product-reorder-prefill): restores the
   * variant option selects and line-item properties, then re-derives the
   * matched variant / price via the existing option-change handler.
   */
  #prefillFromUrl() {
    const { params, active } = read();
    if (!active || !params) return;

    const variant = params.get('variant');
    if (variant) {
      this.refs.variantId.value = variant;
      const matched = this.#variants.find((v) => String(v.id) === variant);
      if (matched && Array.isArray(matched.options)) {
        const selects = this.querySelectorAll('.sb-pdp-simple__option-select');
        selects.forEach((select, i) => {
          if (matched.options[i] != null) select.value = matched.options[i];
        });
      }
    }

    applyProperties(this, params);
    applyArtwork(this, params);

    this.#handleOptionChange();
  }

  #parseDataAttributes() {
    const variantsRaw = this.dataset.variants;
    if (!variantsRaw) return;

    try { this.#variants = JSON.parse(variantsRaw); } catch { this.#variants = []; }
  }

  #cacheRefs() {
    /** @type {SbSimpleProductRefs} */
    this.refs = {};
    const refEls = this.querySelectorAll('[ref]');

    for (const el of refEls) {
      const name = el.getAttribute('ref');
      if (name) this.refs[name] = el;
    }
  }

  #bindEvents() {
    const optionSelects = this.querySelectorAll('.sb-pdp-simple__option-select');

    for (const select of optionSelects) {
      select.addEventListener('change', () => this.#handleOptionChange());
    }

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => this.#handleSubmit(e));
    }
  }

  #bindReviews() {
    const initializeReviewAnchor = () => {
      const reviewEl = this.querySelector(".rating-wrapper");
      const reviewsWidget = document.querySelector('div[id*="stamped_reviews_main_widget"]');
      const reviewBannerBtn = Array.from(document.querySelectorAll('a[href*="#reviews-widget"]'))
      if (!reviewsWidget) return;

      if (reviewEl) {
        reviewEl.style.cursor = 'pointer';
        reviewEl.addEventListener('click', () => {
          reviewsWidget.scrollIntoView({behavior: 'smooth'});
        })
      }

      if (reviewBannerBtn) {
        reviewBannerBtn.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            reviewsWidget.scrollIntoView({behavior: 'smooth'});
          })
        })
      }
    }

    if (document.readyState !== 'loading') {
      initializeReviewAnchor();
    } else {
      window.addEventListener('DOMContentLoaded', initializeReviewAnchor);
    }
  }

  /* Option change: variant matching
     ========================================================================== */

  #handleOptionChange() {
    const selectedOptions = [];
    const optionSelects = this.querySelectorAll('.sb-pdp-simple__option-select');

    for (const select of optionSelects) {
      selectedOptions.push(select.value);
    }

    const matched = this.#variants.find(
      (v) => v.options.toString() === selectedOptions.toString()
    );

    if (matched) {
      this.refs.variantId.value = String(matched.id);

      if (this.refs.productPrice) {
        this.refs.productPrice.textContent = formatMoneyFromCents(matched.price);
      }

      const compare = matched.compare_at_price;
      const hasCompare = typeof compare === 'number' && compare > matched.price;
      if (this.refs.compareAtWrap && this.refs.compareAtPrice) {
        if (hasCompare) {
          this.refs.compareAtPrice.textContent = formatMoneyFromCents(compare);
          this.refs.compareAtWrap.hidden = false;
        } else {
          this.refs.compareAtWrap.hidden = true;
        }
      }

      if (matched.featured_image && this.refs.productImage) {
        this.refs.productImage.src = matched.featured_image.src;
      }
    } else {
      this.refs.variantId.value = '0';
    }
  }

  /* Form submission
     ========================================================================== */

  /** @param {SubmitEvent} e */
  async #handleSubmit(e) {
    e.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const btn = this.refs.addToCartBtn;
    if (btn) btn.disabled = true;
    this.#setButtonText('Processing...');

    try {
      if (!this.refs.variantId?.value || this.refs.variantId.value === '0') {
        throw new Error('No variant selected');
      }

      const formData = new FormData(form);

      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!cartRes.ok) throw new Error(`Cart add failed: HTTP ${cartRes.status}`);

      this.#setButtonText('Added!');
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));

      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      setTimeout(() => {
        if (btn) btn.disabled = false;
        this.#setButtonText('Add to Cart');
      }, 2000);
    } catch (error) {
      console.error('Add to cart failed:', error);
      if (btn) btn.disabled = false;
      this.#setButtonText('Add to Cart');
    }
  }

  /** @param {string} text */
  #setButtonText(text) {
    if (this.refs.addToCartText) {
      this.refs.addToCartText.textContent = text;
    }
  }
}

customElements.define('sb-simple-product-form', SbSimpleProductForm);
